/**
 * useHomeData Hook
 * 
 * Fetches dashboard data for the home screen including
 * portfolio stats, recent opportunities, and attention items.
 * Isolates API calls from UI components per Rule 6.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { authApi } from '../shared/utils/api';
import { logger } from '../shared/utils/logger';
import { Opportunity } from '../shared/types/opportunity';

interface HomeStats {
  portfolioCount: number;
  opportunityCount: number;
  activeCount: number; // evaluating, under_contract
}

interface AttentionItem {
  id: string;
  type: 'overdue' | 'mention' | 'update' | 'status_change';
  title: string;
  subtitle: string;
  opportunityId: number;
  portfolioId: number;
  timestamp: Date;
}

interface RecentOpportunity {
  id: number;
  portfolioId: number;
  name: string;
  updatedAt: Date;
  status: string;
}

interface UseHomeDataResult {
  // Stats
  stats: HomeStats;
  // Recent opportunities
  recentOpportunities: RecentOpportunity[];
  // Attention items
  attentionItems: AttentionItem[];
  // Loading state
  loading: boolean;
  refreshing: boolean;
  refresh: () => Promise<void>;
  error: string | null;
}

export function useHomeData(): UseHomeDataResult {
  const [stats, setStats] = useState<HomeStats>({
    portfolioCount: 0,
    opportunityCount: 0,
    activeCount: 0,
  });
  const [recentOpportunities, setRecentOpportunities] = useState<RecentOpportunity[]>([]);
  const [attentionItems, setAttentionItems] = useState<AttentionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchHomeData = useCallback(async (isRefresh = false) => {
    try {
      setError(null);
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      // Fetch portfolios list
      const portfolioResponse = await authApi.getPortfolios();
      const portfolios = portfolioResponse.portfolios || [];
      
      // Collect all opportunities from all portfolios
      const allOpportunities: (Opportunity & { portfolioId: number })[] = [];
      
      for (const portfolio of portfolios) {
        try {
          const oppResponse = await authApi.getOpportunities(portfolio.id);
          const opportunities = oppResponse.opportunities || [];
          opportunities.forEach(opp => {
            allOpportunities.push({ ...opp, portfolioId: portfolio.id });
          });
        } catch (err) {
          logger.warn('Failed to fetch opportunities for portfolio', 'home', {
            portfolioId: portfolio.id,
          });
        }
      }

      // Calculate stats
      const activeStatuses = ['evaluating', 'under_contract'];
      const activeCount = allOpportunities.filter(o => 
        activeStatuses.includes(o.status || '')
      ).length;

      setStats({
        portfolioCount: portfolios.length,
        opportunityCount: allOpportunities.length,
        activeCount,
      });

      // Get recent opportunities (sorted by updated_at, limit 5)
      const sorted = [...allOpportunities].sort((a, b) => {
        const dateA = a.updated_at ? new Date(a.updated_at).getTime() : 0;
        const dateB = b.updated_at ? new Date(b.updated_at).getTime() : 0;
        return dateB - dateA;
      });

      setRecentOpportunities(
        sorted.slice(0, 5).map(opp => ({
          id: opp.id,
          portfolioId: opp.portfolioId,
          name: opp.name || opp.address || opp.street_address || 'Unnamed',
          updatedAt: opp.updated_at ? new Date(opp.updated_at) : new Date(),
          status: opp.status || 'unknown',
        }))
      );

      // Build attention items from recent updates
      // For now, show opportunities that were recently updated or need action
      const attention: AttentionItem[] = [];
      
      // Opportunities in evaluating status could need attention
      const evaluating = allOpportunities.filter(o => o.status === 'evaluating');
      evaluating.slice(0, 2).forEach(opp => {
        attention.push({
          id: `eval-${opp.id}`,
          type: 'update',
          title: opp.name || opp.address || 'Property',
          subtitle: 'In evaluation - review due diligence',
          opportunityId: opp.id,
          portfolioId: opp.portfolioId,
          timestamp: opp.updated_at ? new Date(opp.updated_at) : new Date(),
        });
      });

      // Under contract items are time-sensitive
      const underContract = allOpportunities.filter(o => o.status === 'under_contract');
      underContract.forEach(opp => {
        attention.push({
          id: `contract-${opp.id}`,
          type: 'status_change',
          title: opp.name || opp.address || 'Property',
          subtitle: 'Under contract - action needed',
          opportunityId: opp.id,
          portfolioId: opp.portfolioId,
          timestamp: opp.updated_at ? new Date(opp.updated_at) : new Date(),
        });
      });

      setAttentionItems(attention.slice(0, 4));

      logger.debug('Home data loaded', 'home', {
        portfolios: portfolios.length,
        opportunities: allOpportunities.length,
        active: activeCount,
        attention: attention.length,
      });

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load data';
      logger.error('Failed to fetch home data', err, 'home');
      setError(message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchHomeData();
  }, [fetchHomeData]);

  const refresh = useCallback(() => fetchHomeData(true), [fetchHomeData]);

  return {
    stats,
    recentOpportunities,
    attentionItems,
    loading,
    refreshing,
    refresh,
    error,
  };
}

/**
 * Format relative time for display
 */
export function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString();
}

